/**
 * Owner: Masa Bayu
 * File: DedicatedCommunityScreen.tsx
 * Date:Time: 2026-10-17 04:34
 * Description: Dedicated QXCircle community router — every circle route rendered as a
 *   purpose-built themed screen wired to the real client API.
 */

import type { ReactElement } from 'react';
import '../../author/dedicated/dedicated.css';
import './community.css';
import { CommunityHome } from './CommunityHome';
import {
  CommunityBlocked,
  CommunityFollowList,
  CommunityMentionFeed,
  CommunityMuted,
  CommunityProfile,
  CommunityTagFeed,
} from './CommunityPeople';
import { CommunityPostDetail } from './CommunityPostDetail';
import { CommunityExplore, CommunityNotifications, CommunitySaved } from './CommunitySections';

export interface DedicatedCommunityScreenProps {
  readonly path: string;
  readonly onNavigate: (path: string) => void;
}

function firstGroup(pattern: RegExp, path: string): string | null {
  const match = pattern.exec(path);
  if (match === null) return null;
  const value = match[1];
  return typeof value === 'string' ? decodeURIComponent(value) : null;
}

export function DedicatedCommunityScreen({
  path,
  onNavigate,
}: DedicatedCommunityScreenProps): ReactElement {
  const postId = firstGroup(/^\/circle\/post\/([^/]+)$/, path);
  const followersHandle = firstGroup(/^\/circle\/profile\/([^/]+)\/followers$/, path);
  const followingHandle = firstGroup(/^\/circle\/profile\/([^/]+)\/following$/, path);
  const profileHandle = firstGroup(/^\/circle\/profile\/([^/]+)$/, path);
  const tag = firstGroup(/^\/circle\/tag\/([^/]+)$/, path);
  const mentionHandle = firstGroup(/^\/circle\/mentions\/([^/]+)$/, path);

  if (postId !== null) {
    return <CommunityPostDetail postId={postId} onNavigate={onNavigate} />;
  }
  if (followersHandle !== null) {
    return (
      <CommunityFollowList handle={followersHandle} kind="followers" onNavigate={onNavigate} />
    );
  }
  if (followingHandle !== null) {
    return (
      <CommunityFollowList handle={followingHandle} kind="following" onNavigate={onNavigate} />
    );
  }
  if (profileHandle !== null) {
    return <CommunityProfile handle={profileHandle} onNavigate={onNavigate} />;
  }
  if (tag !== null) {
    return <CommunityTagFeed tag={tag} onNavigate={onNavigate} />;
  }
  if (mentionHandle !== null) {
    return <CommunityMentionFeed handle={mentionHandle} onNavigate={onNavigate} />;
  }
  if (path === '/circle/explore') return <CommunityExplore onNavigate={onNavigate} />;
  if (path === '/circle/saved') return <CommunitySaved onNavigate={onNavigate} />;
  if (path === '/circle/blocked') return <CommunityBlocked onNavigate={onNavigate} />;
  if (path === '/circle/muted') return <CommunityMuted onNavigate={onNavigate} />;
  if (path === '/circle/notifications') {
    return <CommunityNotifications onNavigate={onNavigate} />;
  }
  return <CommunityHome onNavigate={onNavigate} />;
}
